import React, { useState } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

// Meta categories with their colors and default statuses.
// Colour keys are unchanged; values are the Direction C stage palette:
//   color = stage tile tint · borderColor = tile edge · chipColor = selected chip tint
//   chipBorder = stage dot / selected edge · chipText = stage ink
const INITIAL_META_GROUPS = [
  {
    meta: "New Lead",
    color: "#EAF2FB",
    borderColor: "#C9DEF3",
    chipColor: "#EAF2FB",
    chipBorder: "#1F6FEB",
    chipText: "#1B4F82",
    statuses: [
      { label: "New Lead", preselected: true, mandatory: true },
      { label: "Unassigned", preselected: true },
      { label: "Imported", preselected: false },
    ],
  },
  {
    meta: "Contacted",
    color: "#EFEDFB",
    borderColor: "#D3D0F2",
    chipColor: "#EFEDFB",
    chipBorder: "#6C5CE7",
    chipText: "#4A3FBF",
    statuses: [
      { label: "Attempt Made", preselected: true },
      { label: "Connected", preselected: true },
      { label: "Callback Scheduled", preselected: false },
      { label: "Meeting Scheduled", preselected: false },
    ],
  },
  {
    meta: "Engaged",
    color: "#E6F6F5",
    borderColor: "#BFE6E3",
    chipColor: "#E6F6F5",
    chipBorder: "#17A398",
    chipText: "#11645E",
    statuses: [
      { label: "Interested", preselected: true },
      { label: "Requirements Captured", preselected: false },
      { label: "Properties Shortlisted", preselected: false },
    ],
  },
  {
    meta: "Site visit",
    color: "#FDF4E1",
    borderColor: "#F3E0B4",
    chipColor: "#FDF4E1",
    chipBorder: "#E8A33D",
    chipText: "#8A5A08",
    statuses: [
      { label: "Visit Scheduled", preselected: true },
      { label: "Visit Completed", preselected: true },
      { label: "Revisit Scheduled", preselected: false },
    ],
  },
  {
    meta: "Negotiation",
    color: "#FBEAF3",
    borderColor: "#F2CDE1",
    chipColor: "#FBEAF3",
    chipBorder: "#D6408C",
    chipText: "#9A2F69",
    statuses: [
      { label: "Proposal Sent", preselected: true },
      { label: "Price Negotiation", preselected: false },
      { label: "Terms Discussion", preselected: false },
      { label: "Counter Offer", preselected: false },
    ],
  },
  {
    meta: "Committed",
    color: "#E9F7EE",
    borderColor: "#C4E8D2",
    chipColor: "#E9F7EE",
    chipBorder: "#2FA864",
    chipText: "#1B6B41",
    statuses: [
      { label: "Token Paid", preselected: true },
      { label: "Booking Confirmed", preselected: true },
      { label: "Agreement In Progress", preselected: false },
    ],
  },
  {
    meta: "Closed - Won",
    color: "#E4F5FA",
    borderColor: "#BCE3EF",
    chipColor: "#E4F5FA",
    chipBorder: "#1A9CC2",
    chipText: "#0E5F78",
    statuses: [
      { label: "Agreement Signed", preselected: true },
      { label: "Registered", preselected: true },
      { label: "Possession Given", preselected: false },
    ],
  },
  {
    meta: "Closed - Lost",
    color: "#FBEDEB",
    borderColor: "#F2CFCA",
    chipColor: "#FBEDEB",
    chipBorder: "#D9493D",
    chipText: "#A6342A",
    statuses: [
      { label: "Not Interested", preselected: true },
      { label: "Lost To Competitor", preselected: false },
      { label: "Budget Mismatch", preselected: false },
      { label: "Project Not Suitable", preselected: false },
      { label: "Non-Responsive", preselected: false },
    ],
  },
];

function initGroups() {
  return INITIAL_META_GROUPS.map((g) => ({
    ...g,
    statuses: g.statuses.map((s) => ({
      ...s,
      selected: s.preselected,
      custom: false,
    })),
    addingNew: false,
    newInput: "",
  }));
}

const LeadStatusStep = ({ project, onSubmit, loading }) => {
  const [groups, setGroups] = useState(initGroups);

  const toggleStatus = (gIdx, sIdx) => {
    setGroups((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : {
              ...g,
              statuses: g.statuses.map((s, si) => {
                if (si !== sIdx) return s;

                // cannot deselect mandatory statuses
                if (s.mandatory) return s;

                return {
                  ...s,
                  selected: !s.selected,
                };
              }),
            },
      ),
    );
  };

  const startAdding = (gIdx) => {
    setGroups((prev) =>
      prev.map((g, gi) =>
        gi === gIdx ? { ...g, addingNew: true, newInput: "" } : g,
      ),
    );
  };

  const cancelAdding = (gIdx) => {
    setGroups((prev) =>
      prev.map((g, gi) =>
        gi === gIdx ? { ...g, addingNew: false, newInput: "" } : g,
      ),
    );
  };

  const confirmAdd = (gIdx) => {
    const label = groups[gIdx].newInput.trim();
    if (!label) return;
    setGroups((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : {
              ...g,
              statuses: [
                ...g.statuses,
                { label, selected: true, custom: true, preselected: true },
              ],
              addingNew: false,
              newInput: "",
            },
      ),
    );
  };

  const handleKeyDown = (e, gIdx) => {
    if (e.key === "Enter") confirmAdd(gIdx);
    if (e.key === "Escape") cancelAdding(gIdx);
  };

  const removeCustom = (gIdx, sIdx) => {
    setGroups((prev) =>
      prev.map((g, gi) =>
        gi !== gIdx
          ? g
          : { ...g, statuses: g.statuses.filter((_, si) => si !== sIdx) },
      ),
    );
  };

  const handleSave = () => {
    const result = [];
    groups.forEach((g) => {
      g.statuses
        .filter((s) => s.selected)
        .forEach((s) => result.push({ status: s.label, meta: g.meta }));
    });
    onSubmit(result);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 1, md: 1.5 },
        px: 0,
        maxWidth: 1080,
        fontFamily: FONT,
        color: INK,
      }}
    >
      <Typography
        variant="h5"
        fontWeight={700}
        mb={0.5}
        sx={{ fontFamily: FONT, fontSize: { xs: 24, sm: 27 }, fontWeight: 800, letterSpacing: "-0.028em", color: INK }}
      >
        Add Lead Status Flow
      </Typography>
      <Typography
        variant="body2"
        mb={2.5}
        sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 500, color: "#4A5A6E", textWrap: "pretty" }}
      >
        Select which statuses apply to this project. You can add custom statuses
        under each stage.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          background: "#FFFFFF",
          border: `1px solid ${LINE}`,
          borderRadius: "14px",
          overflow: "hidden",
        }}
      >
        {groups.map((group, gIdx) => (
          <Box
            key={group.meta}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              borderBottom: gIdx === groups.length - 1 ? 0 : "1px solid #F1F4F8",
              py: 1.75,
              px: 2,
              gap: 2,
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {/* Meta Label */}
            <Box
              sx={{
                minWidth: 150,
                width: { xs: "100%", md: 150 },
                flexShrink: 0,
                background: group.color,
                border: `1px solid ${group.borderColor}`,
                borderRadius: "10px",
                p: "9px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontFamily: MONO,
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.11em",
                  textTransform: "uppercase",
                  color: group.chipText,
                }}
              >
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: group.chipBorder }} />
                META
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "14px",
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  color: INK,
                }}
              >
                {group.meta}
              </Typography>
            </Box>

            {/* Chips area */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                pt: { xs: 0, md: "5px" },
              }}
            >
              {group.statuses.map((status, sIdx) => (
                <Box
                  key={sIdx}
                  onClick={() => toggleStatus(gIdx, sIdx)}
                  title={status.mandatory ? "Required status — always included" : undefined}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    minHeight: 34,
                    px: "13px",
                    borderRadius: "999px",
                    border: `1px solid ${status.selected ? group.chipBorder : FIELD_EDGE}`,
                    background: status.selected
                      ? group.chipColor
                      : "#FFFFFF",
                    color: status.selected ? group.chipText : INK_2,
                    fontFamily: FONT,
                    fontSize: "13px",
                    fontWeight: status.selected ? 700 : 500,
                    cursor: status.mandatory ? "not-allowed" : "pointer",
                    userSelect: "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: group.chipBorder,
                      //   color: "#000000",
                      background: status.selected
                        ? group.chipColor
                        : "#F7F9FC",
                    },
                  }}
                >
                  {status.selected && (
                    <CheckIcon sx={{ fontSize: 14, color: group.chipText }} />
                  )}
                  {status.label}
                  {status.mandatory && (
                    <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: MUTED, ml: 0.25 }}>
                      Required
                    </Box>
                  )}
                  {status.custom && (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCustom(gIdx, sIdx);
                      }}
                      sx={{
                        ml: "2px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        opacity: 0.75,
                        "&:hover": { opacity: 1, background: "rgba(15,27,42,0.08)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </Box>
                  )}
                </Box>
              ))}

              {/* Add button / input */}
              {group.addingNew ? (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <input
                    autoFocus
                    value={group.newInput}
                    onChange={(e) =>
                      setGroups((prev) =>
                        prev.map((g, gi) =>
                          gi === gIdx ? { ...g, newInput: e.target.value } : g,
                        ),
                      )
                    }
                    onKeyDown={(e) => handleKeyDown(e, gIdx)}
                    placeholder="Status name..."
                    style={{
                      height: 34,
                      boxSizing: "border-box",
                      border: `1px solid ${group.chipBorder}`,
                      borderRadius: "999px",
                      padding: "0 14px",
                      background: "#FFFFFF",
                      color: INK,
                      fontFamily: FONT,
                      fontSize: "13px",
                      outline: "none",
                      width: "170px",
                      margin: "0",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => confirmAdd(gIdx)}
                    sx={{
                      background: group.chipBorder,
                      border: `1px solid ${group.chipBorder}`,
                      color: "#FFFFFF",
                      width: 30,
                      height: 30,
                      "&:hover": { background: group.chipText },
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => cancelAdding(gIdx)}
                    sx={{
                      background: "#F4F7FA",
                      border: `1px solid ${LINE}`,
                      color: MUTED,
                      width: 30,
                      height: 30,
                      "&:hover": { background: "#EAF2FB" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => startAdding(gIdx)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    minHeight: 34,
                    px: "12px",
                    borderRadius: "999px",
                    border: "1px dashed #B4C1D0",
                    color: MUTED,
                    fontFamily: FONT,
                    fontSize: "12.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: group.chipBorder,
                      color: group.chipText,
                      background: group.chipColor,
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 15 }} />
                  Add
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Submit */}
      <Box mt={2.5} display="flex" justifyContent="flex-end">
        <Button
          className="btn-contained"
          onClick={handleSave}
          disabled={loading}
          endIcon={<ArrowForwardIcon sx={{ fontSize: "17px !important" }} />}
          sx={{
            height: 40,
            px: 2.25,
            borderRadius: "9px !important",
            background: `${PRIMARY} !important`,
            color: "#FFFFFF !important",
            border: "0 !important",
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: "#084767 !important" },
            "&.Mui-disabled": { opacity: 0.6 },
          }}
        >
          {loading ? "Saving..." : "Save & Continue"}
        </Button>
      </Box>
    </Box>
  );
};

export default LeadStatusStep;
